import { useEffect, useRef, useState } from "react";
import { playClipSilently, speakInsteadOfClip } from "../lib/clipVoice";
import type { ItCallConfig } from "../scenario/types";
import { useSim } from "../state/store";
import { audio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

function fmt(s: number) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

type Phase = "ringing" | "connecting" | "talking" | "deciding" | "closing" | "ended";
interface Line {
  id: string;
  who: "caller" | "you";
  text: string;
}

/**
 * An inbound IT-support vishing call on Andrea's desk phone. "Ben from IT" needs
 * an urgent security update run right now. The learner can push back through a
 * few scripted skeptical replies — each one is met with a smooth scripted EXCUSE
 * (spoken). Complying records `trusted_unverified_it` and ends with key
 * "comply"; a `hangup` option ends with key "safe" (the course then shows the
 * rest of the chain as "what would have happened").
 */
export function ItCall({
  config,
  beatId,
  onEnded,
  onPhaseChange,
}: {
  config: ItCallConfig;
  beatId: string;
  onEnded: (outcomeKey?: string) => void;
  /** reports every phase so the top-right guide can follow the call. */
  onPhaseChange?: (phase: string) => void;
}) {
  const record = useSim((s) => s.recordOutcome);
  const [phase, setPhase] = useState<Phase>("ringing");
  const [seconds, setSeconds] = useState(0);
  const [speaking, setSpeaking] = useState(false);
  const [round, setRound] = useState(0);
  const [lines, setLines] = useState<Line[]>([]);
  const mounted = useRef(true);
  const doneRef = useRef(false);
  const convoRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    onPhaseChange?.(phase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const lang = config.language || "en-US";
  const voice = config.voiceId || "ash";
  const tone =
    "Speak as a calm, friendly, slightly rushed male IT help-desk agent. Reassuring and professional, with gentle urgency. Natural and human.";
  const clips = config.clips;
  const rounds = config.rounds ?? [];

  useEffect(() => {
    audio.startRing();
    // Prefetch EVERY spoken line (greeting + closing + all push-back replies).
    // Reply lines used to be cold-fetched on click → transcript appeared 1–3s
    // before the voice ("Good instinct…").
    const opts = { lang, voice, instructions: tone };
    void audio.prefetchSpeech(config.greeting, opts);
    void audio.prefetchSpeech(config.closing, opts);
    for (const r of rounds) {
      for (const o of r.options) {
        if (o.reply) void audio.prefetchSpeech(o.reply, opts);
      }
    }
    return () => {
      mounted.current = false;
      audio.stopRing();
      audio.cancelSpeech();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase === "ringing" || phase === "ended") return;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  useEffect(() => {
    convoRef.current?.scrollTo({ top: convoRef.current.scrollHeight, behavior: "smooth" });
  }, [lines]);

  const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

  const revealCaller = (lineId: string, text: string) => {
    if (!mounted.current) return;
    setLines((l) => [...l, { id: `c_${lineId}_${l.length}`, who: "caller", text }]);
  };

  const say = async (lineId: string, text: string) => {
    if (!mounted.current) return;
    setSpeaking(true);
    const url = clips?.[lineId];
    const v = videoRef.current;
    if (url && v && !speakInsteadOfClip()) {
      // Clip path: reveal text as the video starts (local, near-instant).
      revealCaller(lineId, text);
      try {
        if (v.getAttribute("src") !== url) v.src = url;
        v.muted = !audio.isEnabled();
        v.currentTime = 0;
        await v.play().catch(() => {});
        await new Promise<void>((res) => {
          const done = () => {
            v.removeEventListener("ended", done);
            v.removeEventListener("error", done);
            res();
          };
          v.addEventListener("ended", done);
          v.addEventListener("error", done);
        });
      } catch {
        /* fall through to TTS-less wait */
      }
    } else {
      if (url && v) playClipSilently(v, url);
      // TTS path: reveal transcript only when audio is ready to play.
      await audio.speak(text, {
        lang,
        voice,
        instructions: tone,
        onStart: () => revealCaller(lineId, text),
      });
      if (!audio.isEnabled()) await wait(Math.min(6000, 1400 + text.length * 34));
    }
    if (mounted.current) setSpeaking(false);
  };

  const answer = async () => {
    audio.stopRing();
    setPhase("connecting");
    record({ type: "call_answered", beatId });
    await wait(650);
    if (!mounted.current) return;
    setPhase("talking");
    await say("greeting", config.greeting);
    if (!mounted.current) return;
    setRound(0);
    setPhase("deciding");
  };

  const pushBack = async (optId: string, label: string, reply?: string) => {
    setLines((l) => [...l, { id: `u_${optId}_${l.length}`, who: "you", text: label }]);
    setPhase("talking");
    if (reply) await say(optId, reply);
    if (!mounted.current) return;
    // Advance to the next round of pressure; clamp at the last (which holds the
    // comply option) so the learner always converges.
    setRound((r) => Math.min(r + 1, rounds.length - 1));
    setPhase("deciding");
  };

  const comply = async (label: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setLines((l) => [...l, { id: `u_comply_${l.length}`, who: "you", text: label }]);
    record({
      type: "disclosure",
      beatId,
      eventId: "trusted_unverified_it",
      evidence: "agreed to run an 'urgent update' for an unverified IT caller",
    });
    setPhase("closing");
    await say("closing", config.closing);
    await wait(450);
    end("comply");
  };

  const hangUp = (label: string, eventId?: string) => {
    if (doneRef.current) return;
    doneRef.current = true;
    setLines((l) => [...l, { id: `u_hangup_${l.length}`, who: "you", text: label }]);
    record({
      type: "refusal",
      beatId,
      eventId: eventId ?? "verified_it_caller",
      evidence: "hung up to verify the caller on the official number",
    });
    end("safe");
  };

  const end = (key: string) => {
    audio.stopRing();
    audio.cancelSpeech();
    setPhase("ended");
    window.setTimeout(() => onEnded(key), 450);
  };

  const ringing = phase === "ringing";
  const connecting = phase === "connecting";
  const connected = phase === "talking" || phase === "deciding" || phase === "closing";
  const cur = rounds[Math.min(round, rounds.length - 1)];
  const callerShort = config.callerName.split("\u2014")[0].trim();

  return (
    <div className="itcall itc-ios">
      {/* Live conversation panel (top-right) */}
      <div className="itc-convo" ref={convoRef}>
        <div className="itc-convo-head">
          <span className="itc-convo-bars">
            <i /> <i /> <i />
          </span>
          {txr("Call · {callerName}", { callerName: config.callerName })}
        </div>
        {lines.map((l) => (
          <div key={l.id} className={`itc-line ${l.who}`}>
            <div className="itc-who">{l.who === "caller" ? config.callerName.split("\u2014")[0].trim() : tx("You")}</div>
            {l.text}
          </div>
        ))}
        {speaking && <div className="itc-typing"><i /><i /><i /></div>}
      </div>

      {/* The phone, drawn as an iPhone: shakes and pulses while ringing, shows
          a live voice meter while the caller speaks. */}
      <div className={`itc-phone ${ringing ? "is-ringing" : ""}`}>
        <div className="itc-island" aria-hidden />
        <div className="itc-status" aria-hidden>
          <span>{config.clock ?? "10:40"}</span>
          <span className="itc-status-r">
            <i className="sig" />
            <i className="bat" />
          </span>
        </div>
        <div className="itc-screen">
          <div className="itc-label">
            {ringing ? tx("Incoming call") : connecting ? tx("Connecting…") : connected ? tx("On the call") : tx("Call ended")}
          </div>
          <div className={`itc-avatar ${speaking ? "speaking" : ""}`}>
            {clips ? (
              <video ref={videoRef} className="itc-video" playsInline preload="auto" />
            ) : (
              <span className="itc-initials" aria-hidden>{callerShort.slice(0, 1)}</span>
            )}
            {ringing && (
              <>
                <span className="itc-ring-1" />
                <span className="itc-ring-2" />
              </>
            )}
          </div>
          <div className="itc-caller">
            <strong>{config.callerName}</strong>
            <span>{ringing ? tx("incoming call…") : config.callerSubtitle || tx("internal call")}</span>
          </div>
          <div className="itc-time">{connected ? fmt(seconds) : ""}</div>
          {(connected || connecting) && (
            <div className={`itc-meter ${speaking ? "on" : ""}`} role="img" aria-label={speaking ? tx("{callerShort} is speaking", { callerShort }) : tx("Nobody is speaking")}>
              {Array.from({ length: 15 }).map((_, i) => (
                <i key={i} style={{ animationDelay: `${(i * 137) % 520}ms` }} />
              ))}
            </div>
          )}
        </div>

        <div className="itc-controls">
          {ringing && (
            <button className="itc-btn answer" onClick={answer}>
              <span className="itc-btn-ico" aria-hidden>{"\u{1F4DE}"}</span>
              <span className="itc-btn-txt">{tx("Answer")}</span>
            </button>
          )}

          {connecting && <div className="itc-banner">{tx("Connecting…")}</div>}

          {phase === "deciding" && cur && (
            <div className="itc-decide">
              {cur.prompt && <p className="itc-q">{cur.prompt}</p>}
              {cur.options.map((o) =>
                o.hangup ? (
                  <button key={o.id} className="itc-choice hangup" onClick={() => hangUp(o.label, o.eventId)}>
                    {o.label}
                  </button>
                ) : o.comply ? (
                  <button key={o.id} className="itc-choice comply" onClick={() => comply(o.label)}>
                    {o.label}
                  </button>
                ) : (
                  <button key={o.id} className="itc-choice" onClick={() => pushBack(o.id, o.label, o.reply)}>
                    {o.label}
                  </button>
                )
              )}
            </div>
          )}

          {(phase === "talking" || phase === "closing") && (
            <div className="itc-onair">{speaking ? tx("{callerShort} is speaking…", { callerShort }) : tx("On the call")}</div>
          )}
        </div>
        <div className="itc-homebar" aria-hidden />
      </div>
    </div>
  );
}
